import { useRole } from "hooks/common";
import { useEffect, useState } from "react";
import { nanoid } from "nanoid";
import { useLessonSettings } from "hooks/redux";
import { Space } from "components/Common/Space";
import { Text } from "components/Typography/Text";
import { ClueInput } from "components/Interactive/ClueInput";
import {
  TExerciseIdentifier,
  TExerciseSentence,
  TInputWordsIntoGapsExerciseBlank,
} from "types/app/exercises";
import "./SentenceItem.styles.scss";

type TSentenceItemProps = {
  /**
   * A sentence data to construct an exercise.
   */
  sentence: TExerciseSentence<TInputWordsIntoGapsExerciseBlank>;
  /**
   * The index of this sentence in the list.
   */
  sentenceIndex: number;
  /**
   * A function to handle a change of the sentence.
   */
  onSentenceChange?: (
    sentence: TExerciseSentence<TInputWordsIntoGapsExerciseBlank>,
    sentenceIndex: number
  ) => void;
};

export const SentenceItem: React.FC<TSentenceItemProps> = (props) => {
  const { sentence, sentenceIndex, onSentenceChange } = props;

  const { isUserAdmin, isUserTeacher } = useRole();

  const [inputValues, setInputValues] = useState<
    Record<TExerciseIdentifier, string>
  >({});

  const [isAlreadyCorrected, setIsAlreadyCorrected] = useState(false);

  const {
    isExerciseReadOnlyMode,
    isExercisePreviewMode,
    isExerciseLiveMode,
    viewMode,
  } = useLessonSettings();

  useEffect(() => {
    /*if (isExercisePreviewMode && !isAlreadyCorrected) {
      const updatedChunks = sentence.chunks.map((chunk) => {
        // leave non‑blank chunks untouched
        if (!chunk.blank) return chunk;

        // grab the “correct” answer (or null)
        const correctAnswer = chunk.blank.answers[0] || null;

        // return a brand‑new chunk with its blank.currentAnswer set
        return {
          ...chunk,
          blank: {
            ...chunk.blank,
            currentAnswer: correctAnswer,
            isCorrectAnswer: true,
          },
        };
      });

      // 2) build a new sentence object
      const updatedSentence = {
        ...sentence,
        chunks: updatedChunks,
      };
      setIsAlreadyCorrected(true);
      onSentenceChange?.(updatedSentence);
    }*/
    if (isExerciseLiveMode) {
      const emptyInputValues = sentence.chunks.reduce(
        (acc, chunk) =>
          chunk.blank === null
            ? acc
            : {
                ...acc,
                [chunk.blank.id]: chunk.blank.isCorrectAnswer
                  ? chunk.blank.currentAnswer?.content || ""
                  : "",
              },
        {}
      );
      setInputValues(emptyInputValues);
    }
  }, [
    isExercisePreviewMode,
    isExerciseLiveMode,
    sentence,
    isAlreadyCorrected,
    onSentenceChange,
  ]);

  const clueSelectChangeHandler = (value: string, blankId: string) => {
    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      [blankId]: value,
    }));
  };

  const clueSelectBlurHandler = (value: string, blankId: string) => {
    if (!value) {
      return;
    }

    // Find the specific chunk/blank that was changed
    const targetChunk = sentence.chunks.find(
      (chunk) => chunk.blank?.id === blankId
    );

    if (!targetChunk || !targetChunk.blank) {
      return;
    }

    // Check if the answer is correct ONLY for this specific blank
    const targetAnswer = targetChunk.blank.answers.find(
      (answer) =>
        answer.content.trim().toLowerCase() === value.trim().toLowerCase()
    );

    const isCorrectAnswer = Boolean(targetAnswer);
    const currentAnswer = targetAnswer || {
      id: nanoid(),
      content: value,
    };

    const updatedSentence = {
      ...sentence,
      chunks: sentence.chunks.map((chunk) => ({
        ...chunk,
        ...(chunk.blank?.id === blankId && {
          blank: {
            ...chunk.blank,
            currentAnswer,
            isEmpty: false,
            isCorrectAnswer,
            isAllAttemptsFailed:
              !isCorrectAnswer &&
              chunk.blank.currentAttempt + 1 >= chunk.blank.maxAttempts,
            currentAttempt: chunk.blank?.currentAttempt + 1,
            attempts: [...chunk.blank.attempts, currentAnswer],
          },
        }),
      })),
    };

    onSentenceChange?.(updatedSentence, sentenceIndex);
  };

  return (
    <Space
      direction="horizontal"
      align="center"
      justify="start"
      size={["xx-small", "x-small"]}
      wrap
    >
      {sentence.chunks.map((chunk) =>
        chunk.blank !== null ? (
          <ClueInput
            key={chunk.id}
            type="text"
            name={chunk.blank.id.toString()}
            id={chunk.blank.id.toString()}
            hint={chunk.blank.answers
              .map((answer) => answer.content)
              .join("\n")}
            placeholder={
              viewMode === "constructor" &&
              !isExerciseLiveMode &&
              isUserAdmin &&
              !chunk.blank.isCorrectAnswer
                ? chunk.blank.answers[0]?.content || chunk.blank.hint || ""
                : chunk.blank.hint || ""
            }
            error={chunk.blank.isAllAttemptsFailed}
            success={chunk.blank.isCorrectAnswer}
            disabled={
              chunk.blank.isCorrectAnswer ||
              chunk.blank.isAllAttemptsFailed ||
              isExercisePreviewMode
            }
            value={
              chunk.blank.isAllAttemptsFailed || isExercisePreviewMode
                ? chunk.blank.answers[0]?.content || inputValues[chunk.blank.id]
                : inputValues[chunk.blank.id]
            }
            required
            //fitValue
            onBlur={clueSelectBlurHandler}
            onChange={clueSelectChangeHandler}
            style={{ width: "max-content" }}
            attemptsUsed={chunk.blank.currentAttempt}
            isSuccessful={chunk.blank.isCorrectAnswer}
            attempts={chunk.blank.attempts.map((a) => a.content)}
          />
        ) : (
          <Text key={chunk.id} variant="body2">
            {chunk.text?.content}
          </Text>
        )
      )}
    </Space>
  );
};
